import { StandardEvents } from '@shopify/events';

class EverHonorPersonalization extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.input = this.querySelector('[data-eh-name-input]');
    this.nameOutput = this.querySelector('[data-eh-name-summary]');
    this.variantOutput = this.querySelector('[data-eh-variant-summary]');
    this.counter = this.querySelector('[data-eh-name-count]');
    this.error = this.querySelector('[data-eh-name-error]');
    this.scope = this.closest('.shopify-section');
    this.generation = 0;
    if (!(this.input instanceof HTMLInputElement)) return;
    const { signal } = this.controller;
    this.input.addEventListener('input', this.handleInput, { signal });
    this.input.addEventListener('blur', this.handleBlur, { signal });
    this.input.addEventListener('invalid', this.handleInvalid, { signal });
    this.input.form?.addEventListener('reset', this.handleReset, { signal });
    this.scope?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });
    this.updateName();
    this.updateVariantFromPicker();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.generation += 1;
  }

  handleInput = () => {
    this.updateName();
    this.setError(false);
  };

  handleBlur = () => {
    this.updateName();
    this.setError(!this.input.validity.valid);
  };

  handleInvalid = () => {
    this.setError(true);
  };

  handleReset = () => {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.updateName();
      this.setError(false);
    });
  };

  updateName() {
    const value = this.input.value;
    if (this.counter) this.counter.textContent = String(value.length);
    if (this.nameOutput) this.nameOutput.textContent = value || this.dataset.noName || '';
    const whitespaceOnly = this.input.required && value.trim().length === 0;
    this.input.setCustomValidity(whitespaceOnly ? this.dataset.requiredError || '' : '');
  }

  setError(visible) {
    if (this.error) this.error.hidden = !visible;
    if (visible) this.input.setAttribute('aria-invalid', 'true');
    else this.input.removeAttribute('aria-invalid');
  }

  updateVariantFromPicker(picker = this.scope?.querySelector('variant-picker')) {
    if (!picker || !this.variantOutput) return;
    if (picker.closest('product-card, quick-add-dialog')) return;
    if (picker.dataset.productId && picker.dataset.productId !== this.dataset.productId) return;
    if (typeof picker.getAllSelectedOptions !== 'function') return;
    const options = picker.getAllSelectedOptions();
    if (!Array.isArray(options)) return;
    const values = options.filter((option) => typeof option.value === 'string' && option.value !== 'Default Title');
    if (values.length) {
      this.variantOutput.textContent = values.map((option) => option.name ? `${option.name}: ${option.value}` : option.value).join(' · ');
    }
  }

  handleProductSelect = (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (!picker || !this.scope?.contains(picker)) return;
    if (picker.closest('product-card, quick-add-dialog')) return;
    if (picker.dataset.productId && picker.dataset.productId !== this.dataset.productId) return;
    const generation = ++this.generation;
    const connection = this.controller;
    this.updateVariantFromPicker(picker);
    event.promise?.then(({ detail }) => {
      if (!this.isConnected || connection.signal.aborted || this.controller !== connection || generation !== this.generation) return;
      if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
      if (!this.variantOutput || typeof detail?.resource?.title !== 'string') return;
      const title = detail.resource.title;
      if (title && title !== 'Default Title') this.variantOutput.textContent = title;
      else this.variantOutput.textContent = this.dataset.defaultVariant || '';
    }).catch(() => {});
  };
}

if (!customElements.get('everhonor-personalization')) {
  customElements.define('everhonor-personalization', EverHonorPersonalization);
}
